import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/use-toast';
import { EyeOff, UserCheck, UserPlus } from 'lucide-react';
import StaffAPI from '@/api/staff.api';
import { USER_PERMISSIONS } from 'shared/constants/auth.constants';

const PERMISSION_OPTIONS = Object.values(USER_PERMISSIONS);

/**
 * Action button on an organization row that lets the signed-in staff admin
 * subscribe themselves to that organization. If the admin already has a
 * subscription, an inert "subscribed" indicator (UserCheck) is rendered
 * instead.
 */
export default function SubscribeMeAction({
  meId,
  organizationId,
  organizationName,
  isSubscribed,
  onSubscribed,
}) {
  const [open, setOpen] = useState(false);
  const [permissions, setPermissions] = useState(USER_PERMISSIONS.VIEWER);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleOpenChange = next => {
    if (submitting) return;
    setOpen(next);
    if (!next) setPermissions(USER_PERMISSIONS.VIEWER);
  };

  const onSubmit = async e => {
    e.preventDefault();
    if (!meId || !organizationId || !permissions) return;

    setSubmitting(true);
    try {
      const res = await StaffAPI.createSubscription({
        userId: meId,
        organizationId,
        permissions,
      });

      if (res.error) {
        toast({
          title: 'Failed to subscribe',
          description: res.data?.message || res.error.message || 'Unexpected error',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: `Subscribed to ${organizationName || 'organization'}`,
        description: `${permissions} access added (hidden).`,
      });
      onSubscribed?.(organizationId, permissions);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (isSubscribed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {/*
              Use `aria-disabled` (not `disabled`) so the Radix tooltip still
              receives hover events — a `disabled` button has pointer-events:none
              and the tooltip never shows.
            */}
            <Button
              variant="ghost"
              aria-disabled="true"
              aria-label="Already subscribed"
              className="text-green-600 hover:text-green-600 hover:bg-transparent cursor-default"
              onClick={e => e.preventDefault()}>
              <UserCheck />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Already subscribed</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              aria-label="Subscribe organization"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(true);
              }}>
              <UserPlus />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Subscribe organization</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subscribe to {organizationName || 'this organization'}</DialogTitle>
            <DialogDescription>
              Pick a permission level for your subscription in this organization.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subscribe-me-permissions">Permission level</Label>
              <Select value={permissions} onValueChange={setPermissions} disabled={submitting}>
                <SelectTrigger id="subscribe-me-permissions">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERMISSION_OPTIONS.map(p => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border p-3 bg-muted/40">
              <div className="flex items-start gap-2">
                <EyeOff className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div className="space-y-1">
                  <div className="text-sm font-medium">This subscription will be hidden</div>
                  <div className="text-xs text-muted-foreground">
                    Hidden subscriptions don&apos;t appear in the customer org&apos;s member list or
                    admin lookup. You get access; you just don&apos;t show up as a regular member.
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !meId || !permissions}>
                {submitting ? 'Subscribing...' : 'Subscribe organization'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
